/**
 * Pages API — request contracts.
 *
 *   GET    /v1/schools/:schoolPublicId/pages/:slug              (auth, member)
 *   GET    /v1/public/schools/:schoolPublicId/pages/:slug       (no auth)
 *   GET    /v1/schools/:schoolPublicId/pages/:slug/feed-items          (auth, member)
 *   GET    /v1/schools/:schoolPublicId/pages/:slug/calendar-events     (auth, member)
 *   GET    /v1/public/schools/:schoolPublicId/pages/:slug/feed-items   (no auth)
 *   GET    /v1/admin/schools/:schoolPublicId/pages              (admin)
 *   POST   /v1/admin/schools/:schoolPublicId/pages              (admin)
 *   PATCH  /v1/admin/schools/:schoolPublicId/pages/:pageId      (admin)
 *   POST   /v1/admin/schools/:schoolPublicId/pages/:pageId/publish     (admin)
 *   POST   /v1/admin/schools/:schoolPublicId/page-assets/upload-url    (admin)
 */

import { z } from 'zod'

import { PageTemplateSchema, PageVisibilitySchema } from '../../schemas/entities/page'
import { WebFeedIdSchema } from '../../schemas/entities/web-feed'

const SchoolPublicIdParam = z.object({ schoolPublicId: z.string().min(1) })

export const GetPageBySlugParamsSchema = SchoolPublicIdParam.extend({
  slug: z.string().min(1)
})
export type GetPageBySlugParams = z.infer<typeof GetPageBySlugParamsSchema>

export const GetPublicPageBySlugParamsSchema = GetPageBySlugParamsSchema
export type GetPublicPageBySlugParams = z.infer<typeof GetPublicPageBySlugParamsSchema>

export const ListPagesForSchoolParamsSchema = SchoolPublicIdParam
export type ListPagesForSchoolParams = z.infer<typeof ListPagesForSchoolParamsSchema>

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

export const CreatePageParamsSchema = SchoolPublicIdParam
export type CreatePageParams = z.infer<typeof CreatePageParamsSchema>

export const CreatePageRequestSchema = z.object({
  title: z.string().min(1).max(255),
  slug: z.string().min(1).max(64).regex(SLUG_PATTERN, 'Slug must be lowercase ASCII letters, digits, and hyphens'),
  template: PageTemplateSchema,
  visibility: PageVisibilitySchema.default('PRIVATE')
})
export type CreatePageRequest = z.infer<typeof CreatePageRequestSchema>

export const UpdatePageDraftParamsSchema = SchoolPublicIdParam.extend({
  pageId: z.string().min(1)
})
export type UpdatePageDraftParams = z.infer<typeof UpdatePageDraftParamsSchema>

export const UpdatePageDraftRequestSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    visibility: PageVisibilitySchema.optional(),
    // Optional partial-update field. Omitted for a title/visibility-only edit
    // (e.g. inline rename); the backend only re-validates + writes a draft
    // version when real content is present.
    content: z.unknown().optional()
  })
  // Slug is intentionally NOT in this schema — it's immutable post-creation.
  // The handler explicitly rejects bodies that include `slug` so a stale
  // client gets a clear 400 instead of a silent no-op.
  .strict()
export type UpdatePageDraftRequest = z.infer<typeof UpdatePageDraftRequestSchema>

export const PublishPageParamsSchema = SchoolPublicIdParam.extend({
  pageId: z.string().min(1)
})
export type PublishPageParams = z.infer<typeof PublishPageParamsSchema>

export const DeletePageParamsSchema = SchoolPublicIdParam.extend({
  pageId: z.string().min(1)
})
export type DeletePageParams = z.infer<typeof DeletePageParamsSchema>

export const PublishPageRequestSchema = z.object({
  draftPublicId: z.string().min(1)
})
export type PublishPageRequest = z.infer<typeof PublishPageRequestSchema>

export const PageAssetsUploadUrlParamsSchema = SchoolPublicIdParam
export type PageAssetsUploadUrlParams = z.infer<typeof PageAssetsUploadUrlParamsSchema>

export const PageAssetsUploadUrlRequestSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255)
})
export type PageAssetsUploadUrlRequest = z.infer<typeof PageAssetsUploadUrlRequestSchema>

// ============================================
// Web-feed items read (WEB_FEED page + WebFeed block)
// ============================================

export const GetFeedItemsParamsSchema = SchoolPublicIdParam.extend({
  slug: z.string().min(1)
})
export type GetFeedItemsParams = z.infer<typeof GetFeedItemsParamsSchema>

export const GetPublicFeedItemsParamsSchema = GetFeedItemsParamsSchema
export type GetPublicFeedItemsParams = z.infer<typeof GetPublicFeedItemsParamsSchema>

/**
 * The read is addressed by the page's slug plus the opaque authored `feedId`;
 * the server walks the PUBLISHED content tree for the matching node and reads
 * its url. `.strict()` is load-bearing: a caller-supplied `feedUrl` must be a
 * 400, not a silently-ignored key, or the endpoint reads as a URL fetcher to
 * anyone probing it. The platform never fetches a host a caller named.
 *
 * `refresh=true` skips the cache read and replaces the stored value — what
 * pull-to-refresh on the WEB_FEED page sends. Parsed as a literal string enum
 * (NOT `z.coerce.boolean`, which reads the string `'false'` as `true`).
 */
export const GetFeedItemsQuerySchema = z
  .object({
    feedId: WebFeedIdSchema,
    refresh: z
      .enum(['true', 'false'])
      .transform(v => v === 'true')
      .optional()
  })
  .strict()
export type GetFeedItemsQuery = z.infer<typeof GetFeedItemsQuerySchema>

// ============================================
// iCal calendar-events read (ICAL_FEED page)
// ============================================

/**
 * `GET /v1/schools/:schoolPublicId/pages/:slug/calendar-events` — the
 * page-scoped, authenticated-only read behind an ICAL_FEED page. There is no
 * public twin: an iCal page is always PRIVATE, so no unauthenticated caller can
 * make the platform fetch a school's calendar host.
 *
 * EXTENDS the web-feed items query rather than restating it, so `feedId` and the
 * `refresh` parsing are one definition: a later change to what `refresh` means
 * cannot land on one lane and not the other.
 *
 * NOT `.strict()`, unlike the schema it extends — the strictness is dropped
 * deliberately. The mobile calendar client sends one query builder for both
 * lanes and hardcodes `include_today_placeholder` on every request; under
 * `.strict()` that is an `unrecognized_keys` 400 on every page-lane read. This
 * mirrors `SchoolCalendarQuerySchema`, which also ignores unknown keys. The
 * closed-addressing posture does NOT depend on strictness here: the server
 * resolves the feed url from the page's published version and reads no url off
 * the request, so an ignored `feedUrl` key names nothing the platform will
 * fetch.
 *
 * The window and pagination fields mirror `SchoolCalendarQuerySchema` because
 * the response is that endpoint's `SchoolCalendarResponseSchema` verbatim.
 * `include_today_placeholder` is deliberately NOT declared: the placeholder is a
 * main-calendar affordance, and an undeclared key is simply ignored.
 *
 * `limit`/`offset` are integers, and an EMPTY string falls through to the
 * default rather than 400ing. A bare `z.coerce.number()` turns `''` into 0,
 * which then fails `.min(1)` — so a client that renders `?limit=` for an unset
 * page size would get a validation error instead of the default page. (The main
 * calendar's schema has that same sharp edge; not touched here.)
 */
export const GetPageCalendarEventsQuerySchema = GetFeedItemsQuerySchema.extend({
  start_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format. Expected YYYY-MM-DD')
    .optional(),
  end_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format. Expected YYYY-MM-DD')
    .optional(),
  limit: z.preprocess(v => (v === '' ? undefined : v), z.coerce.number().int().min(1).max(100).optional()),
  offset: z.preprocess(v => (v === '' ? undefined : v), z.coerce.number().int().min(0).optional())
}).strip()
export type GetPageCalendarEventsQuery = z.infer<typeof GetPageCalendarEventsQuerySchema>
